import { useState } from 'react';
import { ChevronDownIcon, DownloadIcon, Loader2Icon } from 'lucide-react';
import type { DocumentModelKindDTO } from '@shared/schemas/document-model';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getChatErrorMessage } from '../chat-errors';
import { downloadRemoteFile } from '../download-remote-file';
import { DocumentGenerationKindIcon } from '../components/ChatDocumentKindIcon';
import { createDocumentGenerationExport } from '../services/document-generation.api';
import { EXPORT_FORMAT_LABELS, getExportFormats, type ExportFormat } from './canvas-config';

type CanvasDownloadMenuProps = {
  conversationId: string;
  disabled: boolean;
  generationId: string;
  kind: DocumentModelKindDTO | null;
};

export function CanvasDownloadMenu({
  conversationId,
  disabled,
  generationId,
  kind,
}: CanvasDownloadMenuProps) {
  const [pendingFormat, setPendingFormat] = useState<ExportFormat | null>(null);
  const formats = kind ? getExportFormats(kind) : [];

  async function handleExport(format: ExportFormat) {
    if (pendingFormat) {
      return;
    }
    setPendingFormat(format);
    try {
      const exported = await createDocumentGenerationExport(conversationId, generationId, { format });
      await downloadRemoteFile(exported.downloadUrl, exported.filename);
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    } finally {
      setPendingFormat(null);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label="Télécharger le document"
          className="h-8 gap-1.5 px-2.5 @lg:px-3"
          disabled={disabled || formats.length === 0}
          size="sm"
          type="button"
          variant="outline"
        >
          {pendingFormat
            ? <Loader2Icon className="size-3.5 animate-spin" aria-hidden />
            : <DownloadIcon className="size-3.5" aria-hidden />}
          <span className="hidden @lg:inline">Télécharger</span>
          <ChevronDownIcon className="size-3 opacity-60" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-auto min-w-44">
        {formats.map((format) => (
          <DropdownMenuItem
            disabled={Boolean(pendingFormat)}
            key={format}
            onSelect={() => void handleExport(format)}
          >
            <DocumentGenerationKindIcon kind={format} size="xs" />
            {EXPORT_FORMAT_LABELS[format]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
